import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AttachmentsModal } from 'pages/Campaigns/components/AttachmentsModal';
import { TableV2, TableV2Column } from 'components/Table/TableV2';

import {
  handleConversationFilterTracking,
  handleConversationSortTracking
} from 'modules/conversations/utils';

import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useTracking } from 'services/tracking/hooks';
import {
  deleteSearchParams,
  updateSearchParams
} from 'services/navigation/paths';
import {
  SearchableConversationSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { useTableSort, useSearchablePagination } from 'common/hooks';
import { SortOrder } from 'react-data-table-component';
import {
  BottomDrawerComponentType,
  useBottomDrawer
} from 'core/stores/bottomDrawer';
import {
  useCampaignConversationsWithSubscriptions,
  useConversationsFilters,
  useConversationNavigation,
  useConversationTagFilters
} from 'modules/conversations/hooks';
import { SearchConversationsItem } from 'modules/conversations/types';
import { getConversationColumns } from 'modules/conversations/utils/getConversationColumns';
import { Campaign } from 'services/campaign/types';
import { filterConversationsColumn } from '../utils/filterConversationsColumn';
import { ConversationFilterIds } from '../constants/enums';
import { getConversationFilterLabel } from '../utils/getConversationFilterLabel';
import { ConversationTagsFilterButton } from './ConversationTagsFilterButton';
import { Box } from '@mui/material';
import { conversationTagsSx } from '../styles';
import { isTagFilterId } from '../utils/tagFilterIds';

export const ConversationTable = ({
  jobTitle,
  location,
  campaign,
  searchName
}: {
  campaign: Campaign;
  searchName: string;
  jobTitle: string;
  location: string;
}) => {
  const [limit, setLimit] = useState(50);
  const [attachmentModalData, setAttachmentModalData] = useState<{
    conversationId: string;
    recipientName: string;
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { track } = useTracking();
  const { openDrawer } = useDrawer();
  const {
    closeBottomDrawer,
    openBottomDrawer,
    isOpen,
    updateBottomDrawerComponentProps
  } = useBottomDrawer();

  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const { selectedTags, toggleTag, clearTag, clearTags, tagFilters } =
    useConversationTagFilters();

  const {
    queryFilter,
    isFiltering,
    appliedFilters,
    setConversationsTableFilters
  } = useConversationsFilters({
    campaignId: campaign.id,
    campaignType: campaign.type,
    searchText: searchName,
    tagFilters
  });

  // After the filters, so the offset can key off the query and any filter change
  // returns to page one.
  const { from, currentPage, handleNext, handlePrevious } =
    useSearchablePagination({
      limit,
      resetKey: JSON.stringify(queryFilter)
    });

  const { sort, handleSort } = useTableSort<
    SearchableConversationSortableFields,
    SearchConversationsItem
  >({
    field: SearchableConversationSortableFields.UpdatedAt,
    direction: SearchableSortDirection.Desc
  });

  const { conversations, total, loading } =
    useCampaignConversationsWithSubscriptions({
      campaignId: campaign.id,
      filter: queryFilter,
      limit,
      sort,
      from
    });

  const {
    total: navigationTotal,
    positionOf,
    moveToConversation,
    updateGlobalIndex
  } = useConversationNavigation({
    filter: queryFilter,
    sort,
    conversationIds: conversations.map((c) => c.id),
    total,
    pageFrom: from
  });

  const onFilterSelect = (filter: {
    value: string | boolean;
    filterId?: string;
  }) => {
    const { filterId: filterIdString, value } = filter;
    const filterId = filterIdString as ConversationFilterIds;

    if (!filterId) {
      return;
    }

    setConversationsTableFilters((prev) => [
      ...(prev?.filter((filter) => filter.filterId !== filterId) ?? []),
      { filterId, value, label: getConversationFilterLabel(filterId, value) }
    ]);

    handleConversationFilterTracking({
      filterId,
      track,
      value
    });
  };

  const handleOnSort = (
    column: TableV2Column<SearchConversationsItem>,
    sortDirection: SortOrder
  ) => {
    handleSort(column, sortDirection);
    if (column.sortField) {
      handleConversationSortTracking({
        track,
        sortField: column.sortField
      });
    }
  };

  const handleRowsPerPageChange = async (newPerPage: number) => {
    setLimit(newPerPage);
  };

  const onApplicationStage = () => {
    setClearSelectedRows(true);
    closeBottomDrawer();
    setTimeout(() => setClearSelectedRows(false), 0); // Reset to false to allow subsequent clears
  };

  const openUpdateApplicationStageDrawer = (
    selectedConversation: SearchConversationsItem
  ) => {
    openDrawer({
      drawerContentType: DrawerComponentType.UpdateApplicationStage,
      drawerComponentProps: {
        conversations: [selectedConversation],
        hideCloseIconButton: true
      }
    });
  };

  const onViewCalendarMeetingDetails = (
    selectedConversation: SearchConversationsItem
  ) => {
    openDrawer({
      drawerContentType: DrawerComponentType.CalendarBookingDetailsDrawer,
      drawerComponentProps: {
        calendarBookingId: selectedConversation.calendarBookingId
      }
    });
  };

  const openConversationDetailsDrawer = (conversationId: string) => {
    const computedIndex = positionOf(conversationId);

    updateGlobalIndex(conversationId);

    updateSearchParams({
      searchParams,
      newParam: {
        key: 'conversationId',
        value: conversationId
      },
      setSearchParams
    });

    openDrawer({
      drawerContentType: DrawerComponentType.ConversationDetails,
      onClose: () => {
        deleteSearchParams({
          searchParams,
          key: 'conversationId',
          setSearchParams
        });
      },
      drawerComponentProps: {
        conversationId,
        jobTitle,
        location,
        moveToConversation,
        conversationsCount: navigationTotal,
        conversationIndex: computedIndex
      }
    });
  };

  const conversationIdParam = searchParams.get('conversationId');

  useEffect(() => {
    // NOTE: This for when you come from the email link
    if (conversationIdParam) {
      openConversationDetailsDrawer(conversationIdParam);
    }
  }, [conversationIdParam, navigationTotal]);

  const columns = useMemo(() => {
    const columns = getConversationColumns(
      openConversationDetailsDrawer,
      openUpdateApplicationStageDrawer,
      setAttachmentModalData,
      onViewCalendarMeetingDetails,
      <ConversationTagsFilterButton
        organizationId={campaign.organizationId}
        selectedTags={selectedTags}
        onToggleTag={toggleTag}
        onClearTags={clearTags}
      />,
      toggleTag,
      selectedTags,
      campaign.type
    );

    const hasCalendarMeeting = conversations.some(
      (conversation) => !!conversation.calendarBookingId
    );

    const hasApplicationSource = conversations.some(
      (conversation) => !!conversation.applicationSource
    );

    return filterConversationsColumn({
      hasCalendarMeeting,
      columns,
      campaign,
      hasApplicationSource
    });
  }, [conversations, openConversationDetailsDrawer, campaign, selectedTags]);

  const onSelectedConversation = (conversations: SearchConversationsItem[]) => {
    if (conversations.length > 0) {
      if (isOpen) {
        updateBottomDrawerComponentProps({
          conversations,
          onClose: onApplicationStage
        });
      } else {
        openBottomDrawer({
          drawerContentType: BottomDrawerComponentType.UpdateConversations,
          drawerComponentProps: {
            conversations,
            onComplete: onApplicationStage,
            campaign
          },
          onClose: onApplicationStage
        });
      }
    } else {
      closeBottomDrawer();
    }
  };

  return (
    <>
      <Box sx={conversationTagsSx.campaignTableTagCells}>
        <TableV2
          columns={columns}
          data={conversations}
          selectableRows
          onSelectedRowsChange={(selected) =>
            onSelectedConversation(selected.selectedRows)
          }
          clearSelectedRows={clearSelectedRows}
          pagination
          paginationServer
          paginationTotalRows={total}
          isLoading={loading}
          sortField={sort.field}
          sortServer
          onSort={handleOnSort}
          isFiltering={isFiltering}
          onFilterSelect={onFilterSelect}
          serverPage={currentPage}
          paginationResetDefaultPage={false}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          onChangeRowsPerPage={handleRowsPerPageChange}
          onRemoveFilters={(filterId) => {
            if (isTagFilterId(filterId)) {
              clearTag(filterId);
              return;
            }

            setConversationsTableFilters((prev) =>
              (prev ?? []).filter((f) => f.filterId !== filterId)
            );
          }}
          filters={appliedFilters}
          onRemoveAllFilters={() => {
            setConversationsTableFilters([]);
            clearTags();
          }}
        />
      </Box>

      {!!attachmentModalData?.conversationId && (
        <AttachmentsModal
          open={!!attachmentModalData?.conversationId}
          handleClose={() => {
            setAttachmentModalData(null);
          }}
          conversationId={attachmentModalData.conversationId}
          recipientName={attachmentModalData.recipientName}
        />
      )}
    </>
  );
};
